/**
 * Brand manifest: shared source of truth for host-to-brand resolution.
 *
 * Consumed by:
 *   - src/config/brand.js      (React, full UI config)
 *   - api/lead-submit.js       (Vercel serverless, attribution)
 *   - src/__tests__/brand-manifest-sync.test.js (validates public/index.html stays in sync)
 *
 * MUST NOT import React, CRA-only modules, or browser-only globals.
 * Keep this file dependency-free so the API function can bundle it.
 */

const BRAND_MANIFEST = {
  cabana: {
    siteOrigin: 'cabanapools.com',
    measurementId: 'G-MSTCFN7WPL',
    serverContainerUrl: 'https://file.cabanapools.com',
    sourceId: '14241174',
    phone: '(888) 817-4115',
    logo: '/images/logo.svg',
    hosts: ['quote.cabanapools.com', 'cabanapools.com'],
  },
  rancho: {
    siteOrigin: 'ranchopools.com',
    measurementId: 'G-X60C8EMXXQ',
    serverContainerUrl: 'https://file.ranchopools.com',
    sourceId: '14420946',
    phone: '(310) 530-8278',
    logo: '/images/brands/rancho-acq.png',
    hosts: ['quote.ranchopools.com', 'ranchopools.com'],
  },
  poollogic: {
    siteOrigin: 'poollogicsd.com',
    measurementId: 'G-LEFH34KZ01',
    serverContainerUrl: 'https://analytics.poollogicsd.com',
    sourceId: '14216270',
    phone: '(619) 913-9335',
    logo: '/images/brands/poollogic-acq.png',
    hosts: ['quote.poollogicsd.com', 'poollogicsd.com'],
  },
  codys: {
    siteOrigin: 'codyspoolservice.com',
    measurementId: 'G-S4V9GEPV26',
    serverContainerUrl: 'https://file.codyspoolservice.com',
    sourceId: '14649951',
    phone: '(916) 730-7918',
    logo: '/images/brands/codys-logo.png',
    hosts: ['quote.codyspoolservice.com', 'codyspoolservice.com'],
  },
};

const DEFAULT_BRAND = 'cabana';

const KNOWN_BRANDS = Object.keys(BRAND_MANIFEST);

const KNOWN_SITE_ORIGINS = new Set(
  Object.values(BRAND_MANIFEST).map(b => b.siteOrigin)
);

function isPreviewHost(host) {
  return host.endsWith('.vercel.app') && host.startsWith('cabana-quote-form');
}

function resolveBrandFromHost(hostname) {
  const host = (hostname || '').toLowerCase();
  if (!host) return null;

  for (const [key, cfg] of Object.entries(BRAND_MANIFEST)) {
    if (cfg.hosts.some(h => host === h)) return key;
  }

  // Suffix match: host ends with .<domain> (covers www., staging., etc.)
  // Non-default brands first so cabana is the natural fallback.
  for (const [key, cfg] of Object.entries(BRAND_MANIFEST)) {
    if (key === DEFAULT_BRAND) continue;
    const domain = cfg.siteOrigin;
    if (host === domain || host.endsWith('.' + domain)) return key;
  }
  const cabDomain = BRAND_MANIFEST[DEFAULT_BRAND].siteOrigin;
  if (host === cabDomain || host.endsWith('.' + cabDomain)) return DEFAULT_BRAND;

  return null;
}

function validateBrandParam(value) {
  const key = (value || '').toLowerCase();
  return BRAND_MANIFEST[key] ? key : null;
}

function resolveBrandKey(brandParam, hostname) {
  // Known production hosts are authoritative: a stray or hostile ?brand= on a
  // real brand host must NOT override it, otherwise the browser would load one
  // brand's GTM/UI while api/lead-submit (also host-authoritative) attributes
  // the lead to a different brand. ?brand= is only honored on preview/unknown
  // hosts (Vercel previews, localhost) where there is no brand host to trust.
  const hostBrand = resolveBrandFromHost(hostname);
  if (hostBrand) return hostBrand;
  const fromParam = validateBrandParam(brandParam);
  if (fromParam) return fromParam;
  return DEFAULT_BRAND;
}

export {
  BRAND_MANIFEST,
  DEFAULT_BRAND,
  KNOWN_BRANDS,
  KNOWN_SITE_ORIGINS,
  isPreviewHost,
  resolveBrandFromHost,
  validateBrandParam,
  resolveBrandKey,
};
